import {
  Box,
  Button,
  CircularProgress,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useFormik } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { setAuthAction } from "src/toolkit/auth/authSlice";
import { setActiveSidebar } from "src/toolkit/sidebar/sidebarSlice";
import {
  useLazySigninQuery,
  useVerifyOtpHandlerMutation
} from "src/toolkit/signin/signinApi";
import { routes } from "src/utils/constants/routes";
import { SidebarOptions } from "src/utils/constants/sidebar";

// import OTPInput from "./components/OTPVerification";
import { useTranslation } from "react-i18next";
import { setAlertDialog } from "src/toolkit/alertDialog/alertDialogSlice";
import HeroImg from "src/components/assets/hero-img.png";
import carchaserWhiteLogo from "src/components/assets/carchaser-white-logo.svg";
import theme from "src/theme";
import { USER_ROLES, USER_ROLES_DOMAINS } from "src/utils/constants/users/userRoles";


const ResetPassword = () => {
  // static data start...
  const signinStates = React.useMemo(() => {
    return {
      credentials: "credentials",
      otpVerification: "otpVerification",
    };
  }, []);
  // static data end...

  const isMobile = useMediaQuery("(max-width:1050px)");

  // useDispatch...
  const dispatch = useDispatch();
  // useDispatch...

  // custom hook calls start...
  const { t } = useTranslation("auth"); // Ensure 'auth' is your translation namespace
  const [signinHandler, signinData] = useLazySigninQuery();
  const [verifyOtpApiHandler, verifyOtpApiData] =
    useVerifyOtpHandlerMutation();
  // custom hook calls end...

  // useState variables start...
  const [signinState, setSigninState] = React.useState(
    signinStates?.credentials
  );
  const [signinUserRole, setSigninUserRole] = React.useState("");
  // useState variables end...

  // Initial form values
  const navigate = useNavigate();
  const initialValues = {
    phone: "",
  };

  // form schema start...
  // Phone number validation schema
  const SignInSchema = Yup.object().shape({
    phone: Yup.string()
      .matches(/^[0-9]{10}$/, t("phone_number_must_be_10_digits")) // Used translation key
      .required(t("phone_number_is_required")), // Used translation key
  });

  const signInFormik = useFormik({
    initialValues,
    validationSchema: SignInSchema,
    onSubmit: async (values) => {

      // await verifyOtpHandler(123456);

      const res = await signinHandler({
        endpoint: `/auth/seller/login`,
        payload: values,
      });

      if (res?.error) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Error!`,
            message: res?.error?.data?.error
              || res?.error?.data?.message || `Some error occured!`
          })
        );
        return;
      }

      if (("status" in res?.data && !res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: `Error!`,
            message: res?.data?.message || `Some error occured!`
          })
        );
        return;
      }

      if (res?.isSuccess && res?.data?.status) {
        dispatch(
          setAuthAction({
            isNewUser: true,
          })
        );
        setSigninState(signinStates?.otpVerification);
      }
    },
  });

  const verifyOtpHandler = async (otp) => {
    const res = await verifyOtpApiHandler({
      endpoint: `/auth/seller/verifylogin`,
      payload: {
        ...signInFormik?.values,
        otp,
      },
    });

    if (!res?.isSuccess || ("status" in res?.data && !res?.data?.status)) {
      dispatch(
        setAlertDialog({
          open: true,
          title: `Error!`,
          message: res?.error?.data?.error || res?.error?.data?.message
            || res?.data?.message || `Some error occured!`
        })
      )
    } else if (res?.isSuccess) {
      sessionStorage.setItem("user_id", res?.data?.UserID);
      sessionStorage.setItem("token", res?.data?.token);

      const { message, ...rest } = res?.data;
      dispatch(setAuthAction(rest));
      dispatch(
        setActiveSidebar(SidebarOptions[SidebarOptions.length - 2])
      );
      navigate(routes.dashboardNavigation);
    }
  };
  // Handlers end...

  // // useEffect start...
  // React.useEffect(() => {
  //   if (signinUserRole) {
  //     dispatch(
  //       setAlertDialog({
  //         open: true,
  //         title: `Alert!`,
  //         message: `Are you sure you want to sign in as ${signinUserRole}?`, // You might want to translate this entire message dynamically if it's user-facing. For now, keeping it as is.
  //         onOk: () => {
  //           window.location = USER_ROLES_DOMAINS[signinUserRole]
  //         }
  //       })
  //     )
  //   }
  // }, [signinUserRole]);
  // useEffect end...

  return (
    <>
      <Grid
        container
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          margin: "0 auto",
        }}
      >
        {!isMobile ? <Grid
          item
          sx={{
            width: "55%",
            borderRadius: "1.5rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "90vh",
            backgroundImage: `url(${HeroImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            p: 4,
            color: "white",
          }}
        >
          {/* <Typography variant="h4" fontWeight="bold">CAR CHASER</Typography> */}
          <img
            src={carchaserWhiteLogo}
            style={{
              width: "18rem",
            }}
          />

          <Box
            sx={{
              padding: "1rem",
              background: "rgba(0, 0, 0, 0.8)",
              borderRadius: "1rem",
            }}
          >
            {/* These contact information texts are hardcoded. If they need translation, they'll need keys in your JSON. */}
            <Typography variant="h6">Contact Information</Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  20-4534 Dufferin St,
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  North York M3H 5X2
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  Mon - Fri: 9am - 7pm
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  Sat: 10am - 5pm | Sun: Closed
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  Phone: 647-893-6208
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  Email: contact@carchaser.ca
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid> : null}
        <Grid
          item
          sx={{
            width: isMobile ? "100%" : "40%",
            padding: "1rem 0 1rem 1rem",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            bgcolor: "#f8f9fa",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: isMobile ? "none" : "45rem",
              padding: "1.5rem 2rem !important",
              boxShadow: 3,
              borderRadius: "1.5rem",
              bgcolor: "white",
            }}
          >
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {t("Reset your password")} {/* Translated */}
            </Typography>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              {t("welcome_to_car_chaser")} {/* Translated */}
            </Typography>
            {signinState === signinStates?.credentials ? (
              <form onSubmit={signInFormik.handleSubmit}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Grid item sx={{ width: "100%" }}>
                    <TextField
                      fullWidth
                      label={t("phone_number")}
                      name="phone"
                      {...signInFormik.getFieldProps("phone")}
                      error={
                        signInFormik.touched.phone &&
                        Boolean(signInFormik.errors.phone)
                      }
                      helperText={
                        signInFormik.touched.phone && signInFormik.errors.phone
                      }
                    />
                  </Grid>
                </Grid>

                {/* The "Car-Chaser Technology..." text is commented out, but if uncommented, it would need translation keys. */}
                {/* <Box mt={2}>
                  <Typography
                    variant="body2"
                    gutterBottom
                    sx={{
                      color: theme.palette.text.primary,
                      padding: "1.5rem 0",
                      lineHeight: "1.35rem",
                    }}
                  >
                    Car-Chaser Technology needs the contact information you
                    provide to us to contact you about our products and
                    services. You may unsubscribe from these communications at
                    any time. For information on how to unsubscribe, as well as
                    our privacy practices and commitment to protecting your
                    privacy, please review our Privacy Policy.
                  </Typography>
                </Box> */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    bgcolor: "#4c34f0",
                    color: "white",
                    borderRadius: "24px",
                    fontSize: "1rem",
                    padding: "12px",
                  }}
                  disabled={signinData?.isFetching}
                >
                  {signinData?.isFetching ? (
                    <CircularProgress
                      size={22}
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                    />
                  ) : (
                    t("Reset") // Translated
                  )}
                </Button>

                <Typography
                  textAlign="center"
                  marginTop={2}
                  sx={{
                    color: theme.palette.text.primary,
                  }}
                >
                  {t("don_t_have_account")}{" "} {/* Translated */}
                  <Button
                    variant="text"
                    sx={{
                      padding: "0px",
                      minWidth: "0px",
                      fontWeight: "bold",
                      color: theme.palette.primary.main,
                      textTransform: "none",
                      textDecoration: "underline",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                    onClick={() => {
                      // Handle sign-in navigation, e.g. navigate("/login")
                      navigate(routes.authSignup);
                    }}
                  >
                    {t("sign_up")} {/* Translated */}
                  </Button>
                </Typography>

                <Typography
                  textAlign="center"
                  marginTop={2}
                  sx={{
                    color: theme.palette.text.primary,
                  }}
                >
                  <Button
                    variant="text"
                    sx={{
                      padding: "0px",
                      minWidth: "0px",
                      fontWeight: "bold",
                      color: theme.palette.primary.main,
                      textTransform: "none",
                      // textDecoration: "underline",
                      "&:hover": {
                        backgroundColor: "transparent",
                        // textDecoration: "underline",
                      },
                    }}
                    onClick={() => {
                      // Handle sign-in navigation, e.g. navigate("/login")
                      // navigate();
                      window.location = USER_ROLES_DOMAINS.dealer;
                    }}
                  >
                    Sign in as a Dealer
                  </Button>
                </Typography>
              </form>
            ) : (
              null
              // <OTPInput
              //   onVerify={verifyOtpHandler}
              //   onResend={signInFormik?.handleSubmit}
              //   isSubmitting={verifyOtpApiData?.isFetching}
              //   // OTPInput's internal texts like "enter_the_verification_code", "otp_verify_button", etc. are assumed to be handled within OTPInput itself using `t()`
              //   // If OTPInput is a custom component, ensure it's also using `useTranslation` to translate its internal strings.
              // />
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default ResetPassword;